import { ListingRequest, ListingResponse } from '@/api/types';
import {
  Account,
  AccountDataRemovalMethod,
  AccountSettings,
  AccountTypeEnum,
  PersonalizedTableViews,
} from '@/client/accounts';
import { Reseller } from '@/client/resellers';
import { MailSystemType, SendingProfileType } from '@/client/sender-profiles';
import { User } from '@/client/users';

export type ListAccountsRequest = ListingRequest;
export type ListAccountsResponse = ListingResponse<Account>;

export type Department = {
  name: string;
};

export type Manager = {
  name: string;
  email: string;
};

export type Country = {
  name: string;
};

export type JobTitle = {
  name: string;
};

export type CompanyName = {
  name: string;
};

export type OfficeLocation = {
  name: string;
};

export type AccountLanguage = {
  name: string;
};

export type SubdomainAccountIdResponse = {
  id: string;
};

export type AccountSAMLConfigReadOnly = {
  identityProviderUrl?: string;
  certificateFingerprint?: string;
  remoteSigninUrl?: string;
  remoteSignoutUrl?: string;
  targetedIdIdentifier?: string;
  firstNameIdentifier?: string;
  lastNameIdentifier?: string;
  emailIdentifier?: string;
};

export type AccountFormState = {
  id?: string;
  name: string;
  subdomain?: string;
  active?: boolean;
  isSSO?: boolean;
  blockSystemEmails?: boolean;
  isFreeTrial?: boolean;
  freeTrialEndsAt?: Date | null;
  freeTrialDeletionDate?: Date | null;
  freeTrialFullAccess?: boolean;
  reseller: Reseller | null;
  mainAccountOfReseller: Reseller | null;
  defaultLanguage: string | null;
  type: boolean;
  hubspotId: string;
  hubspotDomains: string[];
  lifeCycleStage?: string;
  awtStatus?: string;
  phishingStatus?: string;
  companyOwner?: string;
  courseDelivery?: AccountCourseDelivery;
  courseDueDaysCheck?: boolean;
  courseDueDays?: number | null;
  notifyManagerForOverdueCourses?: boolean;
  managerReport?: boolean;
  managerReportFrequency?: AccountManagerReportFrequency;
  courseMinimumScore?: number | null;
  courseReTrainGracePeriodDays?: number | null;
  courseMinimumScoreCheck?: boolean;
  showItSupport?: boolean;
  companyLogo?: string;
  guidelinesFile?: string;
  itSupportSectionTitle?: string;
  itSupportName?: string;
  itSupportTitle?: string;
  itSupportEmail?: string;
  itSupportPhone?: string;
  mailSystem?: MailSystemType | null;
  sendingProfileType?: SendingProfileType;
  isPhishingSenderWhitelisted?: boolean;
  enableUserflow?: boolean;
  companyDescription?: string | null;
  allowPosthogTracking?: boolean;
  linkedInDescription?: string | null;
  timezone?: string | null;
  awarenessGoLiveDate?: Date | null;
};

export type SaveAccountRequest = {
  name: string;
  subdomain?: string;
  active?: boolean;
  isSSO?: boolean;
  blockSystemEmails?: boolean;
  isFreeTrial?: boolean;
  reseller?: string | null;
  defaultLanguage?: string | null;
  type?: AccountTypeEnum | null;
  hubspotId?: string;
  courseDelivery?: AccountCourseDelivery;
  courseDueDays?: number | null;
  courseMinimumScore?: number | null;
  companyLogo?: string | null;
  guidelinesFile?: string | null;
  companyDescription?: string | null;
  branding?: AccountBrandingRequest;
  sendingProfileType?: SendingProfileType;
  enableUserflow?: boolean;
};

export type AccountBrandingRequest = {
  itSupportSectionTitle?: string;
  itSupportName?: string;
  itSupportTitle?: string;
  itSupportEmail?: string;
  itSupportPhone?: string;
};

export type UpdateAccount = {
  name?: string;
  active?: boolean;
  description?: string;
  settings?: AccountSettings;
  isSSO?: boolean;
  blockSystemEmails?: boolean;
  isFreeTrial?: boolean;
  reseller?: string | null;
  defaultLanguage?: string | null;
  type?: AccountTypeEnum | null;
  hubspotId?: string;
  hubspotPrimaryDomain?: string;
  hubspotAdditionalDomains?: string[];
  courseDelivery?: AccountCourseDelivery;
  courseDueDays?: number | null;
  courseMinimumScore?: number | null;
  courseReTrainGracePeriodDays?: number | null;
  notifyManagerForOverdueCourses?: boolean;
  managerReport?: boolean;
  managerReportFrequency?: AccountManagerReportFrequency;
  companyLogo?: string | null;
  guidelinesFile?: string | null;
  branding?: AccountBrandingRequest;
  enableVulnerableUsers?: boolean;
  mailSystem?: MailSystemType | null;
  sendingProfileType?: SendingProfileType;
  isPhishingSenderWhitelisted?: boolean;
  enableUserflow?: boolean;
  companyDescription?: string | null;
  customCompanyDescription?: string | null;
  allowPosthogTracking?: boolean;
  gracePeriodDays?: number;
  dataRemovalMethod?: AccountDataRemovalMethod;
  enableAutoDeleteInactiveUsers?: boolean;
  autoDeleteInactiveUsersDays?: number | null;
  enableAutoPhishingMode?: boolean | null;
  enableAiTrainingForVulnerableUsers?: boolean | null;
  scAutoScheduleEnabled?: boolean;
};

export type UpdateAccountRequest = {
  accountId: string;
  updates: UpdateAccount;
};

export type PostUserRequest = {
  users: string[];
};

export type AccountSendTestEmailRequest = {
  accountId: string;
  email: string;
  notifyId: string;
};

export type ImportAccountUsersRequest = {
  accountId: string;
  fileId: string;
  importKey: string;
  dryRun?: boolean;
  skipNew?: boolean;
  skipExisting?: boolean;
  deleteNotInFile?: boolean;
  restoreSoftDeleted?: boolean;
};

export type ImportSSOConfigRequest = {
  accountId: string;
  displayName?: string;
  importUrl?: string;
  isSSO?: boolean;
};

export type UpdateSSOConfigRequest = {
  accountId: string;
  displayName?: string;
  idpEntityId?: string;
  singleSignOnServiceUrl?: string;
  signingCertificate?: string;
  isSSO?: boolean;
};

export type UpdateGoogleSSOConfigRequest = {
  accountId: string;
  displayName?: string;
  clientId?: string;
  clientSecret?: string;
};

export type ListDuplicatedUsersRequest = ListingRequest;
export type ListDuplicatedUsersResponse = ListingResponse<DuplicatedUsersRow>;

export type DuplicatedUsersRow = {
  email: string;
  users: User[];
};

export type MigrateDuplicatedUsersRequest = {
  accountId: string;
  payload: MigrateDuplicatedUsersPayload;
};

export type MigrateDuplicatedUsersPayload = {
  emails: string[];
};

export type TrainingSettingsFormState = Pick<
  AccountFormState,
  | 'courseMinimumScoreCheck'
  | 'courseMinimumScore'
  | 'courseReTrainGracePeriodDays'
  | 'courseDueDaysCheck'
  | 'courseDueDays'
  | 'notifyManagerForOverdueCourses'
  | 'managerReport'
  | 'managerReportFrequency'
>;

export enum AccountCourseDelivery {
  CP_APP = 'cp-app',
  THIRD_PARTY = 'third-party',
  VIDENBAROMETER = 'videnbarometer',
}

export enum AccountManagerReportFrequency {
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
}

export type UpdateAccountAPIClientRequest = {
  active: boolean;
  regenerate: boolean;
};

export type UpdateAccountTableSettingsRequest = {
  accountId: string;
  personalizedTableViews: PersonalizedTableViews[];
};
